import { EntityId, createSelector } from "@reduxjs/toolkit";

import { RootState } from "app/store";

import { selectPlaybackConfiguration } from "features/config/store/selectors";
import { DateTime } from "luxon";

import { PlaybackConfiguration } from "types/AppConfig";
import { luxonUtils } from "utils/luxonUtils";

const selectSiteId = (state: RootState) => state.playback.siteId;

const selectDeviceIds = (state: RootState) => state.playback.deviceIds;

const selectStartDateTime = (state: RootState) => state.playback.startDateTime;

const selectEndDateTime = (state: RootState) => state.playback.endDateTime;

const selectStartDateTimeError = createSelector(
    [selectStartDateTime, selectEndDateTime],
    (startDateTime?: string, endDateTime?: string) => {
        if (!startDateTime) return;

        const startDateTimeLuxon = DateTime.fromISO(startDateTime);
        const endDateTimeLuxon = luxonUtils.fromIso(endDateTime);

        if (!startDateTimeLuxon.isValid) {
            return "Invalid date specified";
        } else if (
            endDateTimeLuxon?.isValid &&
            startDateTimeLuxon >= endDateTimeLuxon
        ) {
            return "Date must be before TO date";
        }
    },
);

const selectEndDateTimeError = createSelector(
    [selectPlaybackConfiguration, selectStartDateTime, selectEndDateTime],
    (
        playbackConfiguration: PlaybackConfiguration | undefined,
        startDateTime?: string,
        endDateTime?: string,
    ) => {
        if (!endDateTime) return;

        const maximumNumberOfDays = playbackConfiguration?.maxNumberOfDays ?? 6;
        const startDateTimeLuxon = luxonUtils.fromIso(startDateTime);
        const endDateTimeLuxon = luxonUtils.fromIso(endDateTime);

        if (!endDateTimeLuxon?.isValid) {
            return "Invalid date specified";
        } else if (
            startDateTimeLuxon?.isValid &&
            endDateTimeLuxon <= startDateTimeLuxon
        ) {
            return "Date must be after FROM date";
        } else if (
            startDateTimeLuxon?.isValid &&
            endDateTimeLuxon.diff(startDateTimeLuxon, "days").days >
                maximumNumberOfDays
        ) {
            return `Your selection is bigger than ${maximumNumberOfDays} days, please correct`;
        }
    },
);

const selectHasPlaybackSessionRequirementsFulfilled = createSelector(
    [selectSiteId, selectStartDateTimeError, selectEndDateTimeError],
    (
        siteId?: EntityId,
        startDateTimeError?: string,
        endDateTimeError?: string,
    ) => siteId && !startDateTimeError && !endDateTimeError,
);

export const playbackSelectionSelectors = {
    selectSiteId,
    selectDeviceIds,
    selectStartDateTime,
    selectEndDateTime,
    selectStartDateTimeError,
    selectEndDateTimeError,
    selectHasPlaybackSessionRequirementsFulfilled,
};
